import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import { VbLxpAuthService } from './vb-lxp-auth.service';

@Component({
  selector: 'vb-lxp-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [VbLxpAuthService],
  templateUrl: './vb-lxp-login.component.html',
  styleUrl: './vb-lxp-login.component.css',
})
export class VbLxpLoginComponent implements OnInit {
	private readonly authService = inject(VbLxpAuthService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	protected readonly isSubmitting = signal(false);
	protected readonly showPassword = signal(false);
	protected readonly error = signal<string | null>(null);
	protected readonly title = signal('Sign In');
	protected readonly subtitle = signal('Use your credentials to continue');
	protected readonly returnUrl = signal<string | null>(null);

	protected username = '';
	protected password = '';

	private loginPath = 'api/auth/login';
	private redirectTo = '/';
	private defaultUsername = '';

	// Show username for admin routes. For non-admin routes, hide username only when
	// the app provides a default username via route data.
	protected readonly showUsernameField = computed(() => {
		const url = this.returnUrl();
		const isAdminRoute = url === '/admin' || url?.startsWith('/admin/');

		if (isAdminRoute) {
			return true;
		}

		return !this.defaultUsername;
	});

	ngOnInit(): void {
		this.title.set((this.route.snapshot.data['title'] as string) || 'Sign In');
		this.subtitle.set(
			(this.route.snapshot.data['subtitle'] as string) || 'Use your credentials to continue',
		);
		this.loginPath = (this.route.snapshot.data['loginPath'] as string) || 'api/auth/login';
		this.redirectTo = (this.route.snapshot.data['redirectTo'] as string) || '/';
		this.defaultUsername = (this.route.snapshot.data['defaultUsername'] as string) || '';

		// Get returnUrl from query params
		const returnUrlParam = this.route.snapshot.queryParamMap.get('returnUrl');
		this.returnUrl.set(returnUrlParam);

		const url = this.returnUrl();
		const isAdminRoute = url === '/admin' || url?.startsWith('/admin/');

		// For non-admin routes, app may provide a default username for password-only login.
		if (!isAdminRoute && this.defaultUsername) {
			this.username = this.defaultUsername;
		}

		if (this.authService.getToken()) {
			this.router.navigateByUrl(this.redirectTo);
		}
	}

	protected togglePasswordVisibility(): void {
		this.showPassword.update((current) => !current);
	}

	protected onLogin(): void {
		const url = this.returnUrl();
		const isAdminRoute = url === '/admin' || url?.startsWith('/admin/');

		if (!isAdminRoute && this.defaultUsername && !this.username.trim()) {
			this.username = this.defaultUsername;
		}

		if (!this.username.trim() || !this.password.trim()) {
			this.error.set('Username and password are required.');
			return;
		}

		this.error.set(null);
		this.isSubmitting.set(true);

		this.authService
			.login(this.loginPath, this.username.trim(), this.password)
			.pipe(finalize(() => this.isSubmitting.set(false)))
			.subscribe({
				next: (response) => {
					this.authService.saveToken(response.token);
					this.password = '';
					const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
					
					// Preserve query parameters from login page (e.g., ldatatype=mock)
					const loginQueryParams = this.route.snapshot.queryParams;
					const { returnUrl: _, ...preservedParams } = loginQueryParams; // Exclude returnUrl from preserved params
					
					if (returnUrl) {
						// Navigate to return URL with preserved query params
						const urlTree = this.router.parseUrl(returnUrl);
						// Merge preserved params with any params already in returnUrl
						urlTree.queryParams = { ...preservedParams, ...urlTree.queryParams };
						this.router.navigateByUrl(urlTree);
					} else {
						// Navigate to redirect target with preserved query params
						this.router.navigate([this.redirectTo], { queryParams: preservedParams });
					}
				},
				error: (error: { status?: number }) => {
					if (error.status === 401) {
						this.error.set('Invalid username or password.');
						return;
					}

					this.error.set('Unable to sign in right now.');
				},
			});
	}
}
